import { Card, CardProps, Divider, Grid2, IconButton, Typography, useTheme } from "@mui/material";
import { Add, Check, Delete } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { DeleteTimeframeOptions } from "../../../thunks/deleteTimeframe";
import useTimeFormat from "../../../../../hooks/useTimeFormat/useTimeFormat";
import { FloorInventory } from "../../../typings/floor-inventory.entity";
import { Timeframe } from "../../../typings/shared/timeframe";
import { LoadingButtonStyledText } from "../../../../../components/Common/ButtonStyledText.partial";

//  name: "StyledTimeframeDetailCard"
export const StyledTimeframeDetailCard: React.FC<CardProps> = ({ children, sx, ...rest }) => (
  <Card
    sx={{
      width: 420,
      height: 185,
      padding: "16px",
      cursor: "pointer",
      ...sx
    }}
    {...rest}
  >
    {children}
  </Card>
);

/** detail item of timeframe list */
export function DetailTimeframeListItem({
  isDetailOpen,
  timeframe,
  startDate,
  endDate,
  selectedFloor,
  isEditService,
  isEditTimeframe,
  isActive,
  isLast,
  ableToEditServices,
  editServices,
  clickEdit,
  setDeleteModal
}: Readonly<{
  isDetailOpen: boolean;
  timeframe: Timeframe;
  startDate: string;
  endDate: string | null;
  selectedFloor: FloorInventory;
  isEditService: boolean;
  isEditTimeframe: boolean;
  isActive: boolean;
  isLast: boolean;
  ableToEditServices: boolean;
  editServices: (timeframe: Timeframe) => void;
  clickEdit: (timeframe: Timeframe) => Promise<void>;
  setShowTimeframeDetail: (detail: string) => void;
  setDeleteModal: (modal: {
    isOpen: boolean;
    initialValues: DeleteTimeframeOptions;
    isLast?: boolean;
  }) => void;
}>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { dateFormat } = useTimeFormat();

  /** check the usage of each service items */
  const usedBookEquip = false;
  const usedRoomLayout = false;
  const usedCatering = false;

  const handleDeleteTimeframe = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    setDeleteModal({
      isOpen: true,
      initialValues: {
        start: timeframe.start,
        end: timeframe.end,
        fillGapStrategy: "next",
        targetFloorInventoryId: selectedFloor.id
      },
      isLast: isLast
    });
  };

  const handleEditPlan = () => {
    // before loading the time frame, refetch the floor data with the current start day
    clickEdit({
      start: DateTime.fromISO(timeframe.start).toFormat("yyyy-MM-dd"),
      end: timeframe.end ? DateTime.fromISO(timeframe.end).toFormat("yyyy-MM-dd") : null
    });
  };

  const handleEditServicePlan = () => {
    editServices({
      start: DateTime.fromISO(timeframe.start).toFormat("yyyy-MM-dd"),
      end: null
    });
  };

  return (
    <>
      {isDetailOpen && (
        <Grid2
          container
          data-testid="detail-timeframe-parent-grid"
          sx={[
            {
              p: 0.5,
              height: 192,
              // theme.palette.mode === "dark" ? theme.palette.grey[700] : theme.palette.grey[300],
              background: theme.palette.primary.main,
              borderRadius: 1,
              my: 1,
              justifyContent: "space-between",
              alignContent: "center"
            },
            ableToEditServices ? { width: 852 } : { width: 422 }
          ]}
        >
          {/* floor section */}
          <StyledTimeframeDetailCard onClick={handleEditPlan}>
            <Grid2 container sx={{ height: "100%" }} data-testid="detail-timeframe-floor-container">
              {/* header of the selected floor */}
              <Grid2
                container
                sx={{ width: "100%", height: "80%", justifyContent: "space-between" }}
              >
                <Grid2>
                  <Typography variant={"h6"}>{selectedFloor.name}</Typography>
                  <Typography variant={"subtitle1"}>{selectedFloor.level}</Typography>
                </Grid2>

                {/* edit button */}
                <Grid2>
                  <LoadingButtonStyledText
                    theme={theme}
                    data-testid={"timeline-timeframe-select-button"}
                    loading={isEditTimeframe}
                    onClick={handleEditPlan}
                  >
                    {isActive ? t("Show") : t("Edit")}
                  </LoadingButtonStyledText>
                </Grid2>
              </Grid2>

              <Divider
                flexItem
                sx={{
                  width: 1,
                  height: "1px",
                  backgroundColor: theme.palette.text.primary,
                  mb: 0.3,
                  opacity: "0.6"
                }}
              />

              <Grid2
                sx={{
                  display: "flex",
                  width: "100%",
                  alignItems: "center",
                  justifyContent: "space-between"
                }}
              >
                <Grid2 display={"flex"}>
                  <Typography fontWeight={"bold"}>{t("Version")}</Typography>
                  <Typography sx={{ ml: 1, mr: 1 }}>{t("_versionFrom")}</Typography>
                  <Typography>{startDate}</Typography>
                  {endDate && (
                    <>
                      <Typography sx={{ px: 1 }}>-</Typography>
                      <Typography>{DateTime.fromISO(endDate).toFormat(dateFormat)}</Typography>
                    </>
                  )}
                </Grid2>

                {!isActive && (
                  <IconButton
                    data-testid={"timeline-timeframe-delete-button"}
                    onClick={handleDeleteTimeframe}
                  >
                    <Delete />
                  </IconButton>
                )}
              </Grid2>
            </Grid2>
          </StyledTimeframeDetailCard>

          {/* service section */}
          {ableToEditServices && (
            <StyledTimeframeDetailCard onClick={handleEditServicePlan}>
              <Grid2 container sx={{ height: "100%" }}>
                {/* header of the selected floor */}
                <Grid2
                  container
                  sx={{ width: "100%", height: "30%", justifyContent: "space-between" }}
                  data-testid="service-section-header"
                >
                  <Grid2>
                    <Typography variant={"h6"}>{t("Services")}</Typography>
                  </Grid2>

                  {/* edit button */}
                  <Grid2>
                    <LoadingButtonStyledText
                      theme={theme}
                      loading={isEditService}
                      data-testid="timeline-timeframe-editService-button"
                      onClick={handleEditServicePlan}
                    >
                      {t("Assign")}
                    </LoadingButtonStyledText>
                  </Grid2>
                </Grid2>

                <Grid2
                  container
                  sx={{ width: "100%", flexDirection: "column" }}
                  data-testid="service-section-container"
                  gap={0.5}
                >
                  <Grid2 sx={{ display: "flex", alignItems: "center" }}></Grid2>
                  <Grid2 sx={{ display: "flex", alignItems: "center" }}>
                    {usedBookEquip ? <Check fontSize="small" /> : <Add fontSize="small" />}
                    <Typography sx={{ ml: 0.5 }}>{t("Bookable Equipment")}</Typography>
                  </Grid2>

                  <Grid2 sx={{ display: "flex", alignItems: "center" }}>
                    {usedRoomLayout ? <Check fontSize="small" /> : <Add fontSize="small" />}
                    <Typography sx={{ ml: 0.5 }}>{t("Room Layout")}</Typography>
                  </Grid2>

                  <Grid2 sx={{ display: "flex", alignItems: "center" }}>
                    {usedCatering ? <Check fontSize="small" /> : <Add fontSize="small" />}
                    <Typography sx={{ ml: 0.5 }}>{t("Catering")}</Typography>
                  </Grid2>
                </Grid2>
              </Grid2>
            </StyledTimeframeDetailCard>
          )}
        </Grid2>
      )}
    </>
  );
}
